import React from 'react';
import { FlaskConical, Loader2 } from 'lucide-react';
import DrawerShell from '../DrawerShell';
import { formatDateIST } from '../../utils/date';
import {
  ClinicalMilestoneTimeline,
  MilestoneEmpty,
  MilestoneItem,
} from './ClinicalMilestoneTimeline';
import { usePatientClinicalHistory } from './patientHistory.api';

const ACCENT = '#071B34';

/** Milestone timeline of all investigations ordered across visits. */
export default function InvestigationHistoryDrawer({ open, onClose, patientId, patientName }) {
  const { visits, loading } = usePatientClinicalHistory(patientId, open);
  const withTests = visits.filter((v) => (v.investigations || []).length > 0);

  return (
    <DrawerShell
      open={open}
      onClose={onClose}
      title="Investigation History"
      subtitle={patientName ? `${patientName} · labs & imaging by visit` : 'Labs & imaging by visit'}
      eyebrow="IIT Hyderabad Hospital"
      accent={ACCENT}
      icon={<FlaskConical className="h-5 w-5 text-white" />}
      width="lg"
      testId="investigation-history-drawer"
    >
      <div className="p-5">
        {loading ? (
          <div className="py-16 text-center text-[#9AADBE]">
            <Loader2 className="mx-auto h-6 w-6 animate-spin" />
            <p className="mt-2 text-sm">Loading investigations…</p>
          </div>
        ) : withTests.length === 0 ? (
          <MilestoneEmpty icon={FlaskConical} text="No investigations on record yet" />
        ) : (
          <ClinicalMilestoneTimeline accent={ACCENT} testId="investigation-milestone-timeline">
            {withTests.map((v, i) => (
              <MilestoneItem key={v.consultation_id || i} accent={ACCENT} testId={`inv-visit-${i}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-semibold text-[#071B34]">
                    {v.date ? formatDateIST(v.date) : '—'}
                  </p>
                  <span className="rounded border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-sky-800">
                    {(v.investigations || []).length} test{(v.investigations || []).length === 1 ? '' : 's'}
                  </span>
                </div>
                <p className="mt-1 text-[11px] text-[#5A6B7C]">Dr. {v.doctor_name || '—'}</p>
                <ul className="mt-2 space-y-1.5">
                  {(v.investigations || []).map((t, ti) => (
                    <li
                      key={`${t.name}-${ti}`}
                      className="rounded-lg border border-[#D5DEE8] bg-[#F8FAFC] px-2.5 py-2"
                      data-testid={`inv-item-${i}-${ti}`}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold text-[#071B34]">{t.name}</p>
                        {t.priority ? (
                          <span className="rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[9px] font-bold text-amber-800">
                            {t.priority}
                          </span>
                        ) : null}
                      </div>
                      {t.reason ? <p className="mt-0.5 text-[11px] text-[#5A6B7C]">{t.reason}</p> : null}
                      {t.result ? (
                        <p className="mt-0.5 text-[11px] font-medium text-emerald-800">Result: {t.result}</p>
                      ) : (
                        <p className="mt-0.5 text-[10px] italic text-[#9AADBE]">Result pending</p>
                      )}
                    </li>
                  ))}
                </ul>
              </MilestoneItem>
            ))}
          </ClinicalMilestoneTimeline>
        )}
      </div>
    </DrawerShell>
  );
}
